import { QueryList } from '@angular/core';

import {
    EvaluationRange,
    ViewRow,
    WpDisplayConfig,
    WpViewTable,
} from '../../core';
import {
    Cell,
    CellNode,
    Column,
    Context,
    DivRendererOptions,
    GridContent,
    Row,
    RowNode,
    SelectTemplateFn,
    Sort,
    SortOrder,
    sortOrderWithDefault,
} from '../../grid';
import { TranslationKeyMap } from '../main.i18n';
import { ColumnId } from '../model/column-id.model';
import { TableData } from '../model/table-data.model';
import { TemplateId } from '../model/template-id.model';

import { createRowValues, setRowExpanded } from './create-row-values.function';
import { dataForColumn, sortColumn } from './sort-rows.function';

function selectTpl(): SelectTemplateFn {
    return (
        context: Context,
        contents: QueryList<GridContent>,
    ): GridContent => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find((e) => {
                if (e.type !== 'row') {
                    return false;
                }
                let templateId = context.ctx?.templateId;
                if (templateId == null) {
                    templateId =
                        context.r?.cells?.children?.[0].def?.ctx?.templateId ??
                        'header';
                }
                return e.id === templateId;
            });
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        }
    };
}

function compareFn(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    const av = dataForColumn(
        def.columnId as ColumnId,
        rowA.def.ctx.data,
        rowA.def.ctx.table,
        rowA.def.ctx.config,
    );
    const bv = dataForColumn(
        def.columnId as ColumnId,
        rowB.def.ctx.data,
        rowB.def.ctx.table,
        rowB.def.ctx.config,
    );
    return sortColumn(av.value, bv.value, av.type, def.order);
}

function injectLines(lines: string[], el: HTMLElement): void {
    lines.forEach((line, li) => {
        el.appendChild(document.createTextNode(line));
        if (li !== lines.length - 1) {
            el.appendChild(document.createElement('br'));
        }
    });
}

/* TODO revise. copied from chart util */
const MAX_LENGTH = 12;

function _wrapText(
    text: string,
    maxLines: number,
    maxLength: number = MAX_LENGTH,
    concatLast: boolean = true,
): string[] {
    const words = text.split(' ');
    let lines = [];
    let lineLength = 0;
    words.forEach((value, index) => {
        if (value.trim().length === 0) return;
        lines.push(value);
        lineLength += value.length;
        if (index === words.length - 1) return;
        if (
            lineLength >= maxLength ||
            (index + 1 <= words.length &&
                lineLength + words[index + 1].length >= maxLength)
        ) {
            lines.push('<br>');
            lineLength = 0;
        } else {
            lines.push(' ');
            lineLength++;
        }
    });
    lines = lines.join('').split('<br>');
    if (!concatLast) {
        lines = lines.slice(0, maxLines);
    } else {
        if (lines.length > maxLines) {
            const other = lines.slice(maxLines);
            lines[maxLines - 1] += ` ${other.join(' ')}`;
            lines = lines.slice(0, maxLines);
        }
    }
    return lines;
}

function wrapText(el: HTMLElement): void {
    const textEl = el.querySelector(
        '.cmps-coaching-wp-table--row--text',
    ) as HTMLElement;
    if (textEl != null) {
        // TODO handle html better
        const text = textEl.innerHTML.replace(/(<br>|&nbsp;)/gi, ' ');
        const lines = _wrapText(text, 2, text.length / 2);
        textEl.textContent = '';
        injectLines(lines, textEl);
    }
}

function unwrapText(el: HTMLElement): void {
    const textEl = el.querySelector(
        '.cmps-coaching-wp-table--row--text',
    ) as HTMLElement;
    if (textEl != null) {
        // TODO handle html better
        const text = textEl.innerHTML.replace(/(<br>|&nbsp;)/gi, ' ');
        textEl.textContent = '';
        textEl.appendChild(document.createTextNode(text));
    }
}

const excludeMeasure: string[] = [ColumnId.LEFT];

function willMeasure(node: CellNode): void {
    if (node.renderContext?.element != null) {
        node.renderContext.element.classList.add(
            'cmps-coaching-wp-table--measure',
        );
        if (
            node.context?.r?.context?.ctx?.templateId !== TemplateId.CONTENT ||
            !excludeMeasure.includes(node.context?.c?.id)
        ) {
            wrapText(node.renderContext.element);
        }
    }
}

function didMeasure(node: CellNode): void {
    if (node.renderContext?.element != null) {
        node.renderContext.element.classList.remove(
            'cmps-coaching-wp-table--measure',
        );
        if (
            node.context?.r?.context?.ctx?.templateId !== TemplateId.CONTENT ||
            !excludeMeasure.includes(node.context?.c?.id)
        ) {
            unwrapText(node.renderContext.element);
        }
    }
}

const tableColumns: Array<{ id: ColumnId; label: string; canSort: boolean }> = [
    {
        id: ColumnId.LEFT,
        label: TranslationKeyMap._785_PERFORMER,
        canSort: true,
    },
    {
        id: ColumnId.WINNING_PERCENTAGE,
        label: TranslationKeyMap._10094_WINNING_PERCENTAGE,
        canSort: true,
    },
    {
        id: ColumnId.ASSESSED_COMMITS,
        label: TranslationKeyMap._11265_ASSESSED_COMMITS,
        canSort: true,
    },
    {
        id: ColumnId.DISTRIBUTION,
        label: TranslationKeyMap._11269_WINNING_PERCENTAGE_DISTRIBUTION,
        canSort: false,
    },
    {
        id: ColumnId.EXCLUDED_COMMITS,
        label: TranslationKeyMap._11267_EXCLUDED_COMMITS,
        canSort: true,
    },
];

function sliceColumns(start: number, end?: number): Column[] {
    return tableColumns.slice(start, end).map<Column>((e) => {
        return {
            id: e.id,
            width:
                e.id === ColumnId.LEFT
                    ? 275
                    : e.id === ColumnId.DISTRIBUTION
                    ? 1028 +
                      10 /* see margin and border for .cmps-coaching-wp-table--row* */
                    : 125 +
                      10 /* see margin and border for .cmps-coaching-wp-table--row* */,
            height: 50,
            compareFn,
            sticky: e.id === ColumnId.LEFT ? 'left' : undefined,
            ctx: {
                templateId: TemplateId.HEADER,
                data: e,
                className: `cmps-coaching-wp-table--row-header--${e.id}`,
            },
            willMeasure,
            didMeasure,
        };
    });
}

function createRow(
    r: ViewRow,
    ri: number,
    flatColumns: Column[],
    config: WpDisplayConfig,
    table: WpViewTable,
    evalRanges: EvaluationRange[],
    selectedEvalRange: EvaluationRange,
): Row {
    const cells = flatColumns.map<Cell>((e) => {
        return {
            columnId: e.id,
            height: 60,
            ctx: {
                templateId: TemplateId.CONTENT,
                data: r,
            },
            willMeasure,
            didMeasure,
        };
    });

    const row = {
        id: `${r.segmentIdentifier}-${ri}-${Math.random()}`,
        height: 60,
        cells,
        ctx: {
            templateId: TemplateId.CONTENT,
            index: ri,
            data: r,
            table,
            config,
            values: createRowValues(
                r,
                table,
                config,
                evalRanges,
                selectedEvalRange,
            ),
        },
        children: r.children?.map((rr, rri) =>
            createRow(
                rr,
                rri,
                flatColumns,
                config,
                table,
                evalRanges,
                selectedEvalRange,
            ),
        ),
    };

    setRowExpanded(row, r.children != null);

    return row;
}

export function createTableData(
    data: ViewRow[],
    sortOptions: Sort,
    config: WpDisplayConfig,
    table: WpViewTable,
    evalRanges: EvaluationRange[],
    selectedEvalRange: EvaluationRange,
): TableData {
    const columns: Column[] = [
        {
            ...sliceColumns(0, 1)[0],
            sticky: 'left',
            children: [],
        },
        {
            ...sliceColumns(1, 2)[0],
            children: [],
        },
        {
            ...sliceColumns(2, 3)[0],
            children: [],
        },
        {
            ...sliceColumns(3, 4)[0],
            children: [],
        },
        {
            ...sliceColumns(4, 5)[0],
            children: [],
        },
    ];

    const rendererOpts: DivRendererOptions = {
        columns,
        defaultRenderFn: null,
        height: 999999,
        horizontalScrollPadding: 9999,
        verticalScrollPadding: 9999,
    };

    const rows =
        data?.map<Row>((r, ri) =>
            createRow(
                r,
                ri,
                columns,
                config,
                table,
                evalRanges,
                selectedEvalRange,
            ),
        ) ?? [];

    if (rows?.[0]?.ctx?.values != null) {
        rows[0].ctx.values.isFirstRow = true;
    }

    const defaultSortDef: Sort = {
        columnId: ColumnId.LEFT,
        order: SortOrder.ASC,
    };
    const sortDefs = sortOrderWithDefault(
        columns,
        null,
        {
            columnId: columns.find((e) => e.id === sortOptions.columnId).id,
            order: sortOptions.order,
        },
        defaultSortDef,
    );

    return {
        rendererOpts,
        selectTpl: selectTpl(),
        rows,
        sortDefs,
    };
}

export function createRows(
    data: ViewRow[],
    columns: Column[],
    config: WpDisplayConfig,
    table: WpViewTable,
    evalRanges: EvaluationRange[],
    selectedEvalRange: EvaluationRange,
): Row[] {
    return (
        data?.map<Row>((r, ri) =>
            createRow(
                r,
                ri,
                columns,
                config,
                table,
                evalRanges,
                selectedEvalRange,
            ),
        ) ?? []
    );
}
